import { useRef, useContext, createContext } from 'react';
import styled from '@emotion/styled';
import {
  useCheckboxGroup,
  useCheckboxGroupItem,
  useFocusRing,
  useKeyboard,
  mergeProps,
  VisuallyHidden,
} from 'react-aria';
import { useOverlayTriggerState, useCheckboxGroupState } from 'react-stately';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useArgumentValues } from './argumentHooks';
import { useNodeArgumentContext } from '../view-advanced-flow/card/NodeArgumentsContext';

import { argInput } from './argInput';
import { theme } from '../../../theme/theme';
import { scrollbars } from '../../../theme/elements/scrollbars';

import { ArgumentBase, Argument } from './Argument';
import { Icon } from '../../../components/common/Icon';

import { iconCheckmark } from '../../../theme/icons/interface/checkmark';

export function ArgumentMultiselect(props) {
  const { i18n } = useI18n();

  const { localValue, storeValue, setLocalValue } = useArgumentValues({
    props,
  });

  const triggerState = useOverlayTriggerState({
    defaultOpen: props.isFirstArgument && storeValue === undefined,
  });

  const label = props.argument.title ?? i18n.messageFormatter('flow.argument.multiselect.title');

  const { isInvalid, setInvalid } = useNodeArgumentContext({
    key: props.argumentKey,
    onInvalidRequest() {
      return onInvalidRequest(storeValue);
    },
  });

  function onInvalidRequest(value) {
    let isInvalid = false;

    if (
      value == null ||
      (Array.isArray(value) && value.length === 0 && props.argument.required !== false)
    ) {
      isInvalid = true;
    }

    return isInvalid;
  }

  function checkInvalid(value, options) {
    // isInvalid has been touched
    if (isInvalid != null) {
      setInvalid(onInvalidRequest(value), options);
    }
  }

  // This is not relevant since we change the storeValue in handleChange.
  // eslint-disable-next-line no-unused-vars
  function updateCard() {
    // if (storeValue !== localValue) {
    //   const nextValue = localValue != null ? localValue : undefined;
    //   props.onUpdate?.({
    //     argumentKey: props.argumentKey,
    //     value: typeof nextValue === 'string' ? nextValue : undefined,
    //   });
    // }
  }

  function handleChange(value, { preventClose = false } = {}) {
    if (preventClose === false) {
      triggerState.close();
    }

    setLocalValue(value);
    checkInvalid(value, { checkNode: false });

    props.onUpdate?.({
      argumentKey: props.argumentKey,
      value: Array.isArray(value) && value.length > 0 ? value : undefined,
    });
  }

  function handleSaveRequest() {
    triggerState.close();
    // updateCard();
    checkInvalid(localValue, { checkNode: true });
  }

  function handleCloseRequest() {
    handleSaveRequest();
  }

  function handleCancelRequest() {
    triggerState.close();
    // setLocalValue(storeValue);
    checkInvalid(storeValue, { checkNode: true });
  }

  function getLocalValueDisplay() {
    const selectedTitles = props.argument.values
      .filter((argumentValue) => {
        return localValue?.includes(argumentValue.id);
      })
      .map((value) => value.title);

    if (selectedTitles.length === 0) {
      return '';
    }

    if (selectedTitles.length === 1) {
      return selectedTitles[0];
    }

    const lastTitle = selectedTitles.pop();
    const conjunctionWord =
      props.argument.conjunction === 'or'
        ? i18n.messageFormatter('flow.argument.multiselect.or')
        : i18n.messageFormatter('flow.argument.multiselect.and');

    return `${selectedTitles.join(', ')} ${conjunctionWord} ${lastTitle}`;
  }

  const localValueDisplay = getLocalValueDisplay();

  return (
    <Argument
      cardContainerRef={props.cardContainerRef}
      triggerState={triggerState}
      isTriggerDisabled={props.isDisabled}
      isTriggerDisabledStyle={props.isDisabledStyle}
      onCloseRequest={handleCloseRequest}
      onCancelRequest={handleCancelRequest}
      renderTrigger={(triggerRef, triggerProps) => {
        switch (true) {
          case localValue != null:
            return (
              <ArgumentBase {...triggerProps} ref={triggerRef} data-is-invalid={isInvalid}>
                {localValueDisplay}
              </ArgumentBase>
            );
          default:
            return (
              <ArgumentBase
                {...triggerProps}
                ref={triggerRef}
                data-is-invalid={isInvalid}
                data-is-empty={true}
              >
                {props.argumentTypeText}
              </ArgumentBase>
            );
        }
      }}
      renderOverlay={() => {
        return (
          <CheckboxGroup
            label={label}
            value={localValue ?? []}
            values={props.argument.values}
            onChange={handleChange}
            onSaveRequest={handleSaveRequest}
          />
        );
      }}
    />
  );
}

const CheckboxGroupContext = createContext(null);

export function CheckboxGroup(props) {
  const keyboard = useKeyboard({
    onKeyDown(event) {
      // Consume enter and delete key and propagate rest.
      if (event.key === 'Enter') {
        event.preventDefault();
        props.onSaveRequest();
      } else if (event.key === 'Delete') {
        event.preventDefault();
        props.onChange(undefined);
      } else {
        event.continuePropagation();
      }
    },
    onKeyUp(event) {
      // Consume enter and delete key and propagate rest.
      if (event.key === 'Enter') {
        event.preventDefault();
      } else if (event.key === 'Delete') {
        event.preventDefault();
      } else {
        event.continuePropagation();
      }
    },
  });

  const checkboxGroupState = useCheckboxGroupState({
    ...props,
    onChange(value) {
      props.onChange(value, { preventClose: true });
    },
    value: props.value,
  });
  const checkboxGroup = useCheckboxGroup(props, checkboxGroupState);

  return (
    <argInput.InputContainer
      {...checkboxGroup.groupProps}
      {...keyboard.keyboardProps}
      style={{
        '--input-container-padding': '0 0 5px',
      }}
    >
      <S.LabelWrapper>
        <argInput.InputLabel {...checkboxGroup.labelProps} style={{ margin: 0 }}>
          {props.label}
        </argInput.InputLabel>
      </S.LabelWrapper>

      <S.CheckboxGroupContainer>
        <CheckboxGroupContext.Provider value={checkboxGroupState}>
          {props.values.map((value, index) => {
            return (
              <Checkbox key={value.id} value={value.id} name={value.id}>
                {value.title}
              </Checkbox>
            );
          })}
        </CheckboxGroupContext.Provider>
      </S.CheckboxGroupContainer>
    </argInput.InputContainer>
  );
}

function Checkbox(props) {
  const checkboxRef = useRef(null);
  const checkboxGroupState = useContext(CheckboxGroupContext);
  // TODO recheck excludeFromTabOrder on scrollable multiselect. When it was scrollable clicking on
  // an item would jump the scroll sometimes. For some bizarre reason setting excludeFromTabOrder to
  // true fixed that. When we update react-aria we need to recheck this, it might be fixed now.
  const checkboxGroupItem = useCheckboxGroupItem(
    { ...props, excludeFromTabOrder: true },
    checkboxGroupState,
    checkboxRef
  );

  const isSelected = checkboxGroupState.isSelected(props.value);

  const focusRing = useFocusRing();

  return (
    <S.CheckboxLabel>
      <VisuallyHidden>
        <input
          {...mergeProps(checkboxGroupItem.inputProps, focusRing.focusProps)}
          ref={checkboxRef}
        />
      </VisuallyHidden>

      <S.Text>{props.children}</S.Text>
      <S.CheckboxIconWrapper data-is-selected={isSelected}>
        <Icon url={iconCheckmark} color={theme.color.white} size={theme.icon.size_tiny} />
      </S.CheckboxIconWrapper>
    </S.CheckboxLabel>
  );
}

class S {
  static LabelWrapper = styled.div`
    height: 40px;
    border-bottom: 1px solid ${theme.color.line};
    display: flex;
    align-items: center;
    padding: 0 ${theme.su(1)};
  `;

  static CheckboxGroupContainer = styled.div`
    ${scrollbars.dark};
    border-radius: ${theme.borderRadius.default};
    overflow-y: auto;
    max-height: 340px;
  `;

  static CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${theme.color.text};
    font-size: ${theme.fontSize.default};
    height: 40px;
    margin: 0;
    padding: 0 ${theme.su(1)};
    cursor: pointer;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: ${theme.su(1)};
      right: ${theme.su(1)};
      height: 1px;
      background-color: ${theme.color.line};
    }

    &:last-child::before {
      content: none;
    }
  `;

  static Text = styled.div`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    flex: 1 1 auto;
  `;

  static CheckboxIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 2px solid ${theme.color.line};
    background-color: transparent;
    border-radius: ${theme.borderRadius.small};
    flex: 0 0 auto;

    &[data-is-selected='true'] {
      border-color: ${theme.color.blue};
      background-color: ${theme.color.blue};
    }
  `;
}
